import {
  Divider,
  Box,
  Grid,
  Button,
  Stack,
  Typography,
  useTheme,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { useEffect, useState } from "react";
import { addSingleContact, getAllGroups } from "../ContactsService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { CustomInput } from "global/components";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import { ContactFields } from "models/interfaces";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import {
  cleanInput,
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
  timeZones,
} from "helpers/methods";
import { useAppSelector } from "utils/hooks";
import { selectIsFirstNameMandatory } from "redux/generalSlice";

interface CustomProps {
  group?: any;
  isVisible: boolean;
  handleDialogClose: Function;
  filters: any;
  setFilters: Function;
}

const AddContactsModal = (props: CustomProps) => {
  const theme = useTheme();
  const isFirstNameMandatory = useAppSelector(selectIsFirstNameMandatory);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [groups, setGroups] = useState<any>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [fields, setFields] = useState<ContactFields>({
    email: "",
    firstName: "",
    lastName: "",
    contactNo: "",
    address: "",
    postalCode: "",
    city: "",
    state: "",
    country: "",
    tagName: "",
    timeZone: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: any }>({
    email: "",
    group: "",
    firstName: "",
  });

  useEffect(() => {
    getGroups();
  }, []);

  const getGroups = async () => {
    try {
      const res = await getAllGroups();
      setGroups(res);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const addSingleContactHandler = async () => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const body = {
          firstName: fields.firstName,
          lastName: fields.lastName,
          emailId: fields.email.trim(),
          timezone: fields.timeZone,
          address: fields.address,
          postalCode: fields.postalCode,
          city: fields.city,
          state: fields.state,
          country: fields.country,
          contactNo: fields.contactNo,
          tagName: fields.tagName,
        };
        const res = await addSingleContact(
          props?.group ? props.group.id : selectedGroup.id,
          body
        );
        openSuccessNotification(res.message);
        setIsLoading(false);
        handleDialogClose();
        props.setFilters({
          ...props.filters,
          page: 1,
        });
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleOnChangeInputField = (e: any) => {
    const { value } = e.target;
    const cleanedValue = typeof value === "string" ? cleanInput(value) : value;
    setFields({ ...fields, [e.target.name]: cleanedValue });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  const handleDialogClose = () => {
    setFields({
      email: "",
      firstName: "",
      lastName: "",
      contactNo: "",
      address: "",
      postalCode: "",
      city: "",
      state: "",
      country: "",
      tagName: "",
      timeZone: "",
    });
    setSelectedGroup("");
    setErrors({
      email: "",
      group: "",
    });
    props.handleDialogClose();
  };

  const getDialogHeader = () => {
    return (
      <>
        <Box>
          <Typography
            variant="h4"
            fontWeight={600}
            color={theme.palette.primary.main}
            pb={2}
          >
            Create a contact
          </Typography>
          <Divider />
        </Box>
      </>
    );
  };

  const getDialogBody = () => {
    return (
      <>
        <Grid container spacing={2} p={1}>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                label="First Name"
                id="first-name"
                placeHolder="Enter first name"
                type="text"
                name="firstName"
                required={isFirstNameMandatory}
                value={fields.firstName}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.firstName?.length} / 100
                      </span>
                    </InputAdornment>
                  ),
                }}
                error={
                  isFirstNameMandatory &&
                  !isTruthy(fields.firstName) &&
                  errors.firstName
                }
                helperText={
                  isFirstNameMandatory && !isTruthy(fields.firstName)
                    ? errors.firstName
                    : ""
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                id="last-name"
                label="Last Name"
                placeHolder="Enter last name"
                type="text"
                name="lastName"
                value={fields.lastName}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.lastName?.length} / 100
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                required
                id="email"
                label="Email"
                placeHolder="user@example.com"
                type="text"
                name="email"
                value={fields.email.trim()}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 255 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.email?.trim().length} / 255
                      </span>
                    </InputAdornment>
                  ),
                }}
                error={!isTruthy(fields.email) && errors.email && errors.email}
                helperText={
                  !isTruthy(fields.email) && errors.email ? errors.email : ""
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <CustomContactNumberInput
                id="contact"
                label="Contact Number"
                name="contactNo"
                value={fields.contactNo || ""}
                placeHolder="Enter contact number"
                onChange={(phone: any) => {
                  setFields({
                    ...fields,
                    contactNo: phone,
                  });
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                id="address"
                label="Address"
                placeHolder="Enter address"
                type="text"
                name="address"
                value={fields.address}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.address?.length} / 100
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                id="postalCode"
                label="Postal Code"
                placeHolder="Enter postal code"
                type="text"
                name="postalCode"
                value={fields.postalCode}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 20 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.postalCode?.length} / 20
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                id="city"
                label="City"
                placeHolder="Enter city"
                type="text"
                name="city"
                value={fields.city}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.city?.length} / 100
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                id="state"
                label="State"
                placeHolder="Enter state"
                type="text"
                name="state"
                value={fields.state}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.state?.length} / 100
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                id="country"
                label="Country"
                placeHolder="Enter country"
                type="text"
                name="country"
                value={fields.country}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.country?.length} / 100
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomAutoComplete
                id="group-autocomplete"
                label="Time Zone"
                placeHolder="Select timezone"
                textFieldVariant="outlined"
                value={fields.timeZone}
                clearIcon={false}
                options={timeZones.map((timeZone: any) => ({
                  label: timeZone.label,
                }))}
                onChange={(event: any, value: { label: string } | null) => {
                  if (value) {
                    setFields((prevFields) => ({
                      ...prevFields,
                      timeZone: value.label,
                    }));
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <CustomInput
                id="tag"
                label="Tag"
                placeHolder="Enter tag"
                type="text"
                name="tagName"
                value={fields.tagName}
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 1, maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ fontSize: "0.75rem" }}>
                        {fields.tagName?.length} / 100
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              {props.group ? (
                <>
                  <CustomInput
                    id="group"
                    label="Group"
                    type="text"
                    name="group"
                    value={props?.group?.name}
                    disabled={true}
                  />
                </>
              ) : (
                <>
                  <CustomAutoComplete
                    id="group-autocomplete"
                    required
                    label="Select group"
                    placeHolder="Select group"
                    textFieldVariant="outlined"
                    options={groups.map((group: any) => ({
                      label: group.name,
                      id: group.id,
                    }))}
                    value={selectedGroup}
                    onChange={(
                      event: SelectChangeEvent,
                      newValue: { label: string; id: string }
                    ) => {
                      setSelectedGroup(newValue);
                      if (newValue) {
                        setErrors((prev) => ({ ...prev, group: "" }));
                      }
                    }}
                    errorMessage={!isTruthy(selectedGroup) ? errors.group : ""}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </>
    );
  };

  const handleValidation = () => {
    let error = { ...errors };
    let isValid: boolean = true;
    const email = isTruthy(fields.email);
    const firstName = isTruthy(fields.firstName);

    if (!email) {
      error.email = "Please enter email";
      isValid = false;
    }

    if (isFirstNameMandatory && !firstName) {
      error.firstName = "Please enter first name";
      isValid = false;
    }

    if (!props.group && !isTruthy(selectedGroup)) {
      error.group = "Please select group";
      isValid = false;
    }
    setErrors(error);
    return isValid;
  };

  const getFooter = () => {
    return (
      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button
          id="contacts_add__cancel_btn"
          variant="outlined"
          onClick={() => handleDialogClose()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Cancel
        </Button>
        <Button
          id="contacts_add_save_btn"
          variant="contained"
          onClick={() => addSingleContactHandler()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Save
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <CustomDialog
        handleDialogClose={() => handleDialogClose()}
        isDialogOpen={props.isVisible}
        body={getDialogBody()}
        footer={getFooter()}
        title={getDialogHeader()}
        closeButtonVisibility={true}
        closable
        maxWidth="md"
        persistOnClickOutside={true}
      />
    </>
  );
};

export default AddContactsModal;
